import React, { useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const CustomerLogin = () => {
  const navigate = useNavigate();
  const { symbolicName } = useParams();
  const [searchParams] = useSearchParams();  // BOOKING DATE NAVIGATION (Jan 15, 2026)
  const { setToken, setUser, setLoginUser } = useAuth();  // CUSTOMER LOGIN FIX: Add setLoginUser
  
  // Get booking date from URL if present (from confirmation email link)
  const bookingDate = searchParams.get('date');
  
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarName, setCalendarName] = useState('');
  const [calendarId, setCalendarId] = useState('');  // Store calendar ID for scoped login
  const [error, setError] = useState('');

  React.useEffect(() => {
    fetchCalendarInfo();
    // Load saved email
    const savedEmail = localStorage.getItem('rememberedCustomerEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
    }
  }, [symbolicName]);

  const fetchCalendarInfo = async () => {
    try {
      setCalendarLoading(true);
      const response = await axios.get(`${API_BASE_URL}/public/calendars/${symbolicName}`);
      const calendar = response.data.data.calendar;
      setCalendarName(calendar.name);
      setCalendarId(calendar.id);  // Store the calendar ID
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setCalendarName('this calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Save or remove email based on Remember Me checkbox
    if (rememberMe) {
      localStorage.setItem('rememberedCustomerEmail', formData.email);
      localStorage.setItem('stayLoggedIn', 'true');
    } else {
      localStorage.removeItem('rememberedCustomerEmail');
      localStorage.setItem('stayLoggedIn', 'false');
    }

    try {
      // CALENDAR-SCOPED LOGIN (RULE #3):
      // Customer login includes calendar_id for scoped authentication
      // Same email can have different accounts at different calendars
      const loginParams = new URLSearchParams({
        email: formData.email,
        password: formData.password
      });
      
      // Add calendar_id for calendar-scoped customer login
      if (calendarId) {
        loginParams.append('calendar_id', calendarId);
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/auth/login?${loginParams.toString()}`
      );

      const { access_token, user } = response.data;

      // Check if user is a customer
      if (user.role !== 'customer') {
        setError('This login is for customers only. Please use the admin login for calendar management.');
        setLoading(false);
        return;
      }

      // Store token and user info in both localStorage AND AuthContext
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      // CUSTOMER LOGIN FIX (Jan 15, 2026):
      // Must set BOTH loginUser and runningUser for isAuthenticated to work
      // Without setLoginUser, PrivateRoute sees isAuthenticated=false and redirects
      setToken(access_token);
      setLoginUser(user);  // Sets loginUser for isAuthenticated check
      setUser(user);       // Sets runningUser for role checks

      // Store last user role for redirect on session expiry
      localStorage.setItem('lastUserRole', user.role);

      // Register customer to calendar if not already registered
      // (For calendar-scoped users, registration might already exist)
      try {
        await axios.post(
          `${API_BASE_URL}/calendar-registrations`,
          {
            customer_id: user.id,
            calendar_id: calendarId
          },
          { headers: { Authorization: `Bearer ${access_token}` } }
        );
      } catch (regError) {
        // Registration might fail if already registered, that's okay
        console.log('Calendar registration:', regError.response?.data?.detail || 'Already registered or error');
      }

      // Store the calendar symbolic name for logout redirect
      localStorage.setItem('lastCalendarSymbolicName', symbolicName);

      // BOOKING DATE NAVIGATION (Jan 15, 2026):
      // If a booking date was passed in the URL (from confirmation email),
      // include it in the navigation so calendar opens to that date
      const targetUrl = bookingDate 
        ? `/calendar/${symbolicName}?date=${bookingDate}`
        : `/calendar/${symbolicName}`;
      navigate(targetUrl);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Customer Login</h1>
              {calendarLoading ? (
                <p className="text-gray-600">Loading...</p>
              ) : (
                <p className="text-gray-600">Sign in to <span className="font-semibold text-yellow-600">{calendarName}</span></p>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                />
                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                  Stay logged in
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link
                  to={`/customer/register/${symbolicName}`}
                  className="text-yellow-600 hover:text-yellow-700 font-semibold"
                >
                  Create Account
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/find-calendar"
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to Find Calendar
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CustomerLogin;
